import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Contract and Freelance Team Productivity Software | TeamGrid";
const SEO_DESCRIPTION = "Track contractor productivity, accelerate ramp up and protect institutional knowledge with real time workforce insights.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/contract-freelance-teams";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const ContractFreelanceTeams = () => {
  const navigate = useNavigate();
  // FAQ data specific to Contract & Freelance Teams
  const contractFreelanceFAQs = [
    {
      question:
        "Can TeamGrid help compare freelancer vs internal team productivity?",
      answer:
        "Yes, TeamGrid highlights productivity patterns that make contract vs internal work more measurable.",
    },
    {
      question: "Does TeamGrid require manual input for contractors?",
      answer:
        "No, TeamGrid auto-captures activity without extra work from freelancers or internal staff.",
    },
    {
      question: "Is TeamGrid suitable for hybrid workforce teams?",
      answer:
        "Yes, it supports productivity visibility across mixed teams of freelancers and full-time staff.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Can TeamGrid help compare freelancer and internal team productivity?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid highlights productivity patterns that make contract and internal work more comparable." } },
            { "@type": "Question", name: "Does TeamGrid require manual input for contractors?", acceptedAnswer: { "@type": "Answer", text: "No, TeamGrid automatically captures productivity signals without requiring additional input from freelancers or internal staff." } },
            { "@type": "Question", name: "Is TeamGrid suitable for hybrid workforce teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid supports productivity visibility across hybrid teams that include freelancers and full-time employees." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="Contract & Freelance Teams"
      heroTitle="Productivity Insights Software for <span style='color: #00D1C1; font-weight: 600;'>Contract & Freelance Teams</span>"
      heroSubtitle="Maximize Value From Your Flexible Workforce—Without Losing Control or Knowledge"
      primaryButtonText="Get a Demo for Contract & Freelance Teams"
      customPrimaryButton={customButton}
      fullSolutionContentTitle="Optimize Your Contract Workforce"
      fullSolutionContentDescription="TeamGrid helps teams track contractor productivity, speed up ramp-up, and protect institutional knowledge through productivity benchmarking, ramp-up tracking, knowledge retention mapping, and hybrid team collaboration analysis."
      fullSolutionContentOutcomes="Achieve 25% improvement in contractor ROI, 40% faster contractor productivity ramp-up, 35% reduction in post-contract knowledge gaps, 18% reduction in contingent workforce spend, and 28% improvement in hybrid team collaboration."
      hideProductivity={true}
      hideFeatures={true}
      hideOutcomes={true}
      hideWhyChoose={true}
        faqs={contractFreelanceFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for Contract & Freelance Teams."
      />
    </>
  );
};

export default ContractFreelanceTeams;
